import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { take } from 'rxjs';
import { Board } from '../../core/board/board';
import { BoardHolderService } from '../../core/board/board.holder.service';
import { BoardService } from '../../core/board/board.service';
import { StripeProductType } from '../../core/stripe/product.stripe';
import { StripeService } from '../../core/stripe/stripe.service';
import { EmailToken } from '../../core/workspace/workspace';
import { WorkspaceFuncs } from '../../core/workspace/workspace.func';
import { WorkspaceHolderService } from '../../core/workspace/workspace.holder.service';
import { BoardIconTitleComponent } from '../../domain/board/board-icon-title/board-icon-title.component';
import { UpgradeToPremiumComponent } from '../../domain/membership/upgrade-to-premium/upgrade-to-premium.component';
import { InviteTeamMemberListComponent } from '../../domain/team-member/invite-team-member-list/invite-team-member-list.component';
import { BasicPageComponent } from '../_shared/basic-page/basic-page.component';

@Component({
  selector: 'app-members',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzStepsModule,
    NzButtonModule,
    NzIconModule,
    NzSkeletonModule,
    NzInputModule,
    NzTableModule,
    MatTooltipModule,
    NzTagModule,
    BasicPageComponent,
    InviteTeamMemberListComponent,
    BoardIconTitleComponent,
    UpgradeToPremiumComponent,
  ],
  templateUrl: './members.component.html',
  styleUrl: './members.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MembersComponent {
  constructor(
    private route: Router,
    private stripeService: StripeService,
    private boardService: BoardService,
    private boardholderService: BoardHolderService,
    private workspaceHolderService: WorkspaceHolderService
  ) {}

  userList = toSignal(this.boardService.getAllUserList$(), { initialValue: [] });
  removedUserList = toSignal(this.boardService.getRemovedUserList$(), { initialValue: [] });

  isPremium = computed(() => {
    return this.workspaceHolderService.paidStatus()?.type === StripeProductType.premium;
  });

  get availableMemberCount() {
    return this.stripeService.availableMemberCount();
  }

  getInviteMembersTooltip() {
    if (this.availableMemberCount <= this.userList().length) {
      return 'Please purchase additional memberships to invite more people.';
    }
    return `You can invite ${this.availableMemberCount - this.userList().length} more members.`;
  }

  isInviteTeamMemberVisible = false;

  isCreator() {
    return this.boardholderService.isCreator();
  }

  isCreatorBoard(user: Board) {
    return WorkspaceFuncs.isCreator({
      workspace: this.workspaceHolderService.currentWorkspace(),
      board: user,
    });
  }

  pendingEmailList = computed(() => {
    return this.workspaceHolderService.currentWorkspace().pendingEmailList;
  });

  onInvitePeopleClick() {
    this.isInviteTeamMemberVisible = true;
  }

  onDetailsUserBoardClick() {
    // TODO
  }

  onResendEmailClick(item: EmailToken) {
    this.workspaceHolderService.resendEmail$(item).pipe(take(1)).subscribe();
  }

  onRemovePendingEmailClick(emailToken: EmailToken) {
    this.workspaceHolderService.currentWorkspace().pendingEmailList = this.workspaceHolderService
      .currentWorkspace()
      .pendingEmailList!.filter((e) => e.email !== emailToken.email);
    this.workspaceHolderService.updateCurrentWorkspace$().pipe(take(1)).subscribe();
  }

  onRemoveUserBoardClick(board: Board) {
    this.boardService.removeFromWorkspace$(board).pipe(take(1)).subscribe();
  }
}
